import { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';

import { Menu, type MenuProps } from 'antd';
import cn from 'classnames';

import * as route from '@/shared/constants/router';
import { useMobileMediaQuery } from '@/shared/lib/hooks';

import PatternLabIcon from '@/shared/assets/icons/pattern-lab-icon.svg';
import PatternLiveIcon from '@/shared/assets/icons/pattern-live-icon.svg';
import PatternOverviewIcon from '@/shared/assets/icons/pattern-overview-icon.svg';

import cls from './pattern-studio-menu.module.css';

type MenuItem = Required<MenuProps>['items'][number];

export const PatternStudioMenu = memo(() => {
    const { t } = useTranslation('pattern-studio');
    const { pathname } = useLocation();
    const isMobile = useMobileMediaQuery();

    const selectedKeys = useMemo(() => [pathname], [pathname]);

    const menuItems = useMemo<MenuItem[]>(
        () => [
            {
                key: route.getPatternStudioOverviewPath(),
                label: (
                    <Link
                        data-tour="pattern-overview-menu-item"
                        to={route.getPatternStudioOverviewPath()}
                    >
                        <PatternOverviewIcon
                            height={16}
                            width={16}
                        />
                        {t('overview')}
                    </Link>
                ),
            },
            {
                key: route.getPatternStudioLivePath(),
                label: (
                    <Link
                        data-tour="pattern-live-menu-item"
                        to={route.getPatternStudioLivePath()}
                    >
                        <PatternLiveIcon
                            height={26}
                            width={26}
                        />
                        {t('patternLive')}
                    </Link>
                ),
            },
            {
                key: route.getPatternStudioLabPath(),
                label: (
                    <Link
                        data-tour="pattern-lab-menu-item"
                        to={route.getPatternStudioLabPath()}
                    >
                        <PatternLabIcon
                            height={26}
                            width={26}
                        />
                        {t('patternLab')}
                    </Link>
                ),
            },
        ],
        [t],
    );

    return (
        <div className={cn(cls.wrapper)}>
            <div
                className={cn(cls.menuContainer, {
                    [cls.mobileMenu]: isMobile,
                })}
                data-tour="pattern-studio-menu"
            >
                <Menu
                    className={cls.menu}
                    id="PatternStudioMenu"
                    items={menuItems}
                    mode="inline"
                    selectedKeys={selectedKeys}
                />
            </div>
        </div>
    );
});

PatternStudioMenu.displayName = 'PatternStudioMenu';
